// src/services/statusCatalogService.js
//
// Catálogo de estados (nombre + color) en Firestore `config/claimStatuses`
// (lectura: cualquier usuario autorizado; escritura: solo admin, firestore.rules)
// y recuperación, a pedido del admin, del estado original de Organized desde el
// historial de cada reclamo. Ver config/claimStatuses.js.
import { db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, setDoc, writeBatch, Timestamp } from 'firebase/firestore';
import { getAllClaimRecords } from './claimRecordService';
import { getAdjusters } from './adjusterService';
import { buildOrganizedPlan } from '../migration/organizedImport';
import { DEFAULT_STATUSES, sanitizeCatalog, statusFromHistoryReason } from '../config/claimStatuses';

const CATALOG_DOC = ['config', 'claimStatuses'];

export const getStatusCatalog = async () => {
  const snapshot = await getDoc(doc(db, ...CATALOG_DOC));
  const items = snapshot.exists() ? sanitizeCatalog(snapshot.data().items) : [];
  return snapshot.exists() ? items : DEFAULT_STATUSES;
};

export const saveStatusCatalog = async (items, byEmail) => {
  const clean = sanitizeCatalog(items);
  await setDoc(doc(db, ...CATALOG_DOC), { items: clean, updatedAt: Timestamp.now(), updatedBy: byEmail || null });
  return clean;
};

// Reclamos sin `oaStatus` cuyo historial dice con qué estado de Organized
// entraron. Solo LEE (vista previa); applyOaStatusRecovery escribe.
export const previewOaStatusRecovery = async onProgress => {
  const records = await getDocs(collection(db, 'claimRecords'));
  const pending = records.docs.filter(item => !item.data().oaStatus);
  const found = [];
  const BATCH = 25; // lecturas en paralelo
  for (let start = 0; start < pending.length; start += BATCH) {
    const chunk = pending.slice(start, start + BATCH);
    const results = await Promise.all(chunk.map(async item => {
      const history = await getDocs(collection(db, 'claimRecords', item.id, 'flowHistory'));
      const statuses = history.docs.map(entry => statusFromHistoryReason(entry.data().reason)).filter(Boolean);
      return statuses.length > 0 ? { id: item.id, clientName: item.data().clientName || '', oaStatus: statuses[statuses.length - 1] } : null;
    }));
    found.push(...results.filter(Boolean));
    if (onProgress) onProgress(Math.min(start + BATCH, pending.length), pending.length);
  }
  return { total: records.size, withoutStatus: pending.length, found };
};

// Estados desde el Excel de Organized: mismo cruce que Sync OA (Claim # y
// respaldo por cliente/dirección) pero SOLO propone el campo oaStatus de los
// reclamos que aún no lo tienen. No crea reclamos ni cambia PAs ni pasos.
export const planOaStatusFromExcel = async rows => {
  const [claimRecords, adjusters] = await Promise.all([getAllClaimRecords(), getAdjusters()]);
  const plan = buildOrganizedPlan(rows, claimRecords, adjusters);
  const items = plan.fill
    .filter(item => item.diffs && item.diffs.oaStatus)
    .map(item => ({ id: item.claimRecord.id, clientName: item.claimRecord.clientName || '', oaStatus: item.diffs.oaStatus }));
  return { rows: rows.length, matched: plan.fill.length, items };
};

export const applyOaStatusRecovery = async (found, onProgress) => {
  let done = 0;
  for (let start = 0; start < found.length; start += 400) {
    const batch = writeBatch(db);
    found.slice(start, start + 400).forEach(item => batch.update(doc(db, 'claimRecords', item.id), { oaStatus: item.oaStatus }));
    await batch.commit();
    done += Math.min(400, found.length - start);
    if (onProgress) onProgress(done, found.length);
  }
  return done;
};
